import * as moment from 'moment';

/**
 * Shared logic for cardholder tables that render a fixed set of columns (Name, Email,
 * Phone/Mobile Number, Physical/Digital/Both, ...) plus whatever dynamic product-form
 * fields the backend returns as `headers` (column labels) and per-row `responses`
 * (field label/value pairs). Used by EditCardholderGroupComponent's cardholder table and
 * every "selected cardholders" table in the orders flow (orders-edit, orders-create, and
 * the order-creation wizard's cardholder/preview steps) so they all resolve and label
 * dynamic columns the same way instead of re-implementing this per component.
 */
export class DynamicCardholderColumnsUtil {
  /**
   * Rebuilds a `displayedColumns` array from a component's fixed base columns plus any
   * dynamic headers the API returned, inserting the dynamic columns just before the first
   * checkbox-style column (Physical/Digital/Both — whichever appears first in `baseColumns`)
   * so identity fields (Name/Email/Phone) stay left-most and status/action columns stay
   * right-most. Always rebuilds from `baseColumns` rather than mutating the previous
   * `displayedColumns`, so repeated calls (pagination, refetches) never duplicate columns.
   *
   * @param baseColumns Fixed columns the component always renders with dedicated markup.
   * @param headers Raw header labels returned by the API for this fetch.
   * @param aliasKeys Dynamic header keys that duplicate a fixed column under a different
   *   name (e.g. a "Mobile Number" header duplicating a fixed "Phone" column) and so must
   *   be dropped rather than rendered as an extra dynamic column.
   * @param extraTrailingColumns Extra fixed columns to append at the end when a condition
   *   holds (e.g. orders-edit's "MifareCode" column, shown only for certain order statuses).
   */
  static buildDisplayedColumns(
    baseColumns: string[],
    headers: any[] = [],
    aliasKeys: string[] = [],
    extraTrailingColumns: string[] = []
  ): { columns: string[]; dynamicColumnKeys: string[] } {
    const dynamicColumnKeys = Array.from(
      new Set(
        (headers ?? [])
          .map((header: any) => (header ?? '').toString().replace(/\s+/g, ''))
          .filter(
            (key: string) =>
              !!key && !baseColumns.includes(key) && !aliasKeys.includes(key)
          )
      )
    );

    const columns = [...baseColumns];
    const checkboxColumnIndexes = ['Physical', 'Digital', 'Both']
      .map((column) => columns.indexOf(column))
      .filter((index) => index !== -1);
    const insertIndex = checkboxColumnIndexes.length
      ? Math.min(...checkboxColumnIndexes)
      : columns.length;
    columns.splice(insertIndex, 0, ...dynamicColumnKeys);

    extraTrailingColumns.forEach((column) => {
      if (!columns.includes(column)) {
        columns.push(column);
      }
    });

    return { columns, dynamicColumnKeys };
  }

  /** Only the dynamic (non-fixed) columns in `displayedColumns` — for `@for` template loops. */
  static dynamicColumnsOf(
    displayedColumns: string[],
    baseColumns: string[],
    extraTrailingColumns: string[] = []
  ): string[] {
    return displayedColumns.filter(
      (column) => !baseColumns.includes(column) && !extraTrailingColumns.includes(column)
    );
  }

  /** Resolves a dynamic column's display label back from the raw API headers. */
  static getHeaderLabel(headers: any[], key: string): string {
    let labelValue = '';
    (headers ?? []).forEach((item: any) => {
      if ((item ?? '').toString().replace(/\s+/g, '') === key.replace(/\s+/g, '')) {
        labelValue = item;
      }
    });
    return labelValue;
  }

  /**
   * Resolves a row's value for a dynamic column out of its `responses` array.
   * @param dateFallbackFormatter Used when the value is a date moment can't parse directly
   *   (e.g. HelpersService.cardholderDateFormat) — kept as a callback so this stays
   *   independent of Angular DI.
   */
  static getFieldValue(
    dynamicResponse: any[] = [],
    columnLabel: string,
    dateFallbackFormatter?: (value: any) => string
  ): string {
    let colValue: any;
    (dynamicResponse ?? []).forEach((item: any) => {
      if (item?.label?.replace(/\s+/g, '') === columnLabel.replace(/\s+/g, '')) {
        if (item?.value) {
          if (item.type === 'Date') {
            const formatted = moment(item.value).format('DD/MM/YYYY');
            colValue =
              formatted !== 'Invalid date'
                ? formatted
                : (dateFallbackFormatter?.(item.value) ?? item.value);
          } else {
            colValue = item.value ?? '';
          }
        } else {
          colValue = '';
        }
      }
    });
    return colValue ?? '';
  }
}
